import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { FaRupeeSign, FaCheckCircle, FaUpload, FaQrcode } from 'react-icons/fa';
import apiService from '../services/api';

const PaymentPage = () => {
  const { consultationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  useEffect(() => {
    // Get payment details from navigation state or fetch from API
    if (location.state?.paymentDetails) {
      setPaymentDetails(location.state.paymentDetails);
    } else {
      fetchConsultationDetails();
    }
  }, [consultationId]);

  const fetchConsultationDetails = async () => {
    try {
      const response = await apiService.getConsultation(consultationId);
      if (response.data.success) {
        const data = response.data.data;
        setPaymentDetails({
          amount: data.amount,
          upi_id: data.upi_id || 'educonsult@paytm',
          qr_code_url: data.qr_code_url || '/uploads/qr_codes/payment_qr.png'
        });
      }
    } catch (error) {
      console.error('Error fetching consultation details:', error);
      toast.error('Failed to load payment details');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        toast.error('Please upload only JPG, JPEG, or PNG images');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!screenshot) {
      toast.error('Please select a payment screenshot');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('screenshot', screenshot);
      formData.append('payment_id', consultationId); // Using consultation ID as payment ID
      formData.append('transaction_id', transactionId);

      const response = await apiService.uploadPayment(formData);

      if (response.data.success) {
        setUploaded(true);
        toast.success('Payment screenshot uploaded successfully! We will verify it shortly.');
      }
    } catch (error) {
      console.error('Error uploading payment screenshot:', error);
      toast.error(error.response?.data?.error || 'Failed to upload screenshot');
    } finally {
      setUploading(false);
    }
  };

  if (!paymentDetails) {
    return (
      <div className="container-custom py-20">
        <div className="flex justify-center items-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Payment - EduConsult</title>
        <meta name="description" content="Complete your consultation payment" />
      </Helmet>

      <section className="hero-pattern pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Complete <span className="gradient-text">Payment</span>
            </h1>
            <p className="text-lg text-gray-600">
              Pay the consultation fee to proceed with expert guidance
            </p>
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Steps Indicator */}
            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center opacity-50">
                  <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">
                    <FaCheckCircle />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Registration</div>
                    <div className="text-xs text-gray-500">Completed</div>
                  </div>
                </div>
                <div className="h-px flex-1 bg-green-500 mx-4"></div>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                    2
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Payment</div>
                    <div className="text-xs text-gray-500">In progress</div>
                  </div>
                </div>
                <div className="h-px flex-1 bg-gray-300 mx-4"></div>
                <div className="flex items-center opacity-50">
                  <div className="w-10 h-10 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center font-bold mr-3">
                    3
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Consultation</div>
                    <div className="text-xs text-gray-500">Pending</div>
                  </div>
                </div>
              </div>
            </div>

            {!uploaded ? (
              <div className="grid md:grid-cols-2 gap-8">
                {/* Payment Instructions */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Instructions</h2>

                  {/* Amount */}
                  <div className="bg-primary-50 border-2 border-primary-200 rounded-xl p-6 mb-6">
                    <div className="text-sm text-gray-600 mb-2">Consultation Fee</div>
                    <div className="flex items-center text-3xl font-bold text-primary-600">
                      <FaRupeeSign className="text-2xl" />
                      {paymentDetails.amount}
                    </div>
                  </div>

                  {/* QR Code */}
                  <div className="text-center mb-6">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center justify-center">
                      <FaQrcode className="mr-2" /> Scan QR Code
                    </h3>
                    <div className="inline-block bg-white p-4 rounded-lg shadow-md">
                      <img
                        src={paymentDetails.qr_code_url}
                        alt="UPI QR Code"
                        className="w-64 h-64 object-contain"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/256x256?text=QR+Code';
                        }}
                      />
                    </div>
                  </div>

                  {/* UPI ID */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-sm text-gray-600 mb-2">UPI ID</div>
                    <div className="font-mono font-semibold text-gray-900 flex items-center justify-between">
                      <span>{paymentDetails.upi_id}</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(paymentDetails.upi_id);
                          toast.success('UPI ID copied!');
                        }}
                        className="text-xs bg-primary-600 text-white px-3 py-1 rounded"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="mt-6 bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">
                      <strong>Note:</strong> After making the payment, please upload the screenshot below to complete the process.
                    </p>
                  </div>
                </div>

                {/* Upload Screenshot */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Upload Payment Proof</h2>

                  {/* Transaction ID */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction ID (Optional)
                    </label>
                    <input
                      type="text"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      placeholder="Enter UPI transaction ID"
                      className="w-full input-field"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Screenshot <span className="text-red-500">*</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleFileChange}
                        className="hidden"
                        id="screenshot-upload"
                      />
                      <label htmlFor="screenshot-upload" className="cursor-pointer">
                        <FaUpload className="text-4xl text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 mb-1">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-sm text-gray-500">
                          JPG, JPEG, or PNG (Max 5MB)
                        </p>
                      </label>
                    </div>
                  </div>

                  {/* Preview */}
                  {previewUrl && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preview
                      </label>
                      <img
                        src={previewUrl}
                        alt="Payment screenshot preview"
                        className="w-full h-auto rounded-lg border-2 border-gray-200"
                      />
                    </div>
                  )}

                  {/* Upload Button */}
                  <button
                    onClick={handleUpload}
                    disabled={!screenshot || uploading}
                    className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploading ? 'Uploading...' : 'Submit Payment Proof'}
                  </button>

                  <p className="mt-4 text-xs text-gray-500 text-center">
                    Your payment will be verified within 24 hours
                  </p>
                </div>
              </div>
            ) : (
              /* Success Message */
              <div className="bg-white rounded-xl p-12 shadow-lg text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <FaCheckCircle className="text-green-500 text-4xl" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Payment Submitted!</h2>
                <p className="text-lg text-gray-600 mb-8">
                  Thank you! Your payment screenshot has been uploaded successfully.
                  We will verify it within 24 hours and contact you soon.
                </p>
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
                  <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                  <ul className="text-left space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Our team will verify your payment within 24 hours
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      You'll receive a confirmation email/SMS once verified
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-600 mr-2">•</span>
                      Our expert counselor will contact you to schedule consultation
                    </li>
                  </ul>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="btn btn-primary"
                >
                  Back to Home
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default PaymentPage;
